const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const sumPresent = (transactions, key) => {
  const present = transactions.filter(
    (row) => row[key] != null && row[key] !== ""
  );
  if (present.length === 0) return null;
  return present.reduce((total, row) => total + num(row[key]), 0);
};

const samePercent = (transactions, key) => {
  const present = transactions.filter(
    (row) => row[key] != null && row[key] !== ""
  );
  if (present.length === 0) return null;
  const first = num(present[0][key]);
  const allSame = present.every((row) => num(row[key]) === first);
  return allSame ? first : null;
};

const emptyRow = () => ({
  fromAccountNumber: "",
  status: null,
  settledAt: null,
  merchantFeePercent: null,
  merchantFee: null,
  xpayFeePercent: null,
  xpayFee: null,
  spayFee: null,
  processingFeePercent: null,
  processingFee: null,
  afterFeeAmount: null,
  settleIds: "",
  settleState: "NONE",
  settleParts: [],
});

const SETTLE_LABEL = {
  SETTLED: "Өндөрлөсөн",
  WAITING: "Хүлээгдэж буй",
  FAILED: "Амжилтгүй",
  NONE: "Өндөрлөөгүй",
};

const transactionSettleState = (row) => {
  if (row.status === "SETTLED") return "SETTLED";
  if (row.status === "FAILED") return "FAILED";
  if (row.merchantSettleId) return "WAITING";
  return "NONE";
};

/**
 * Payout truth is transaction.status. merchantSettleId is which batch it sits in.
 * Mixed transactions stay listed; they are not collapsed into one label.
 */
const settleView = (transactions) => {
  const ids = [
    ...new Set(
      transactions
        .map((row) => row.merchantSettleId)
        .filter((id) => id != null && id !== "")
    ),
  ];
  const states = transactions.map(transactionSettleState);
  const uniqueStates = [...new Set(states)];
  if (uniqueStates.length <= 1) {
    return {
      settleIds: ids.join(", "),
      settleState: uniqueStates[0] || "NONE",
      settleParts: [],
    };
  }

  const seen = new Set();
  const settleParts = [];
  transactions.forEach((row) => {
    const state = transactionSettleState(row);
    const id = row.merchantSettleId || null;
    const key = `${id ?? "-"}:${state}`;
    if (seen.has(key)) return;
    seen.add(key);
    settleParts.push({ id, state });
  });

  return {
    settleIds: ids.join(", "),
    settleState: "MIXED",
    settleParts,
  };
};

/**
 * One invoice row for Гүйлгээний хяналт.
 * Amounts sum across items. A percent is shown only when every transaction agrees.
 * afterFeeAmount is the stored value, not recomputed.
 */
export function invoiceControlRow(invoice) {
  const transactions = (invoice?.invoiceItems || [])
    .map((item) => item?.transaction)
    .filter(Boolean);

  if (transactions.length === 0) return emptyRow();

  const accounts = [
    ...new Set(
      transactions.map((row) => row.fromAccountNumber).filter(Boolean)
    ),
  ];

  const statuses = [
    ...new Set(transactions.map((row) => row.status).filter(Boolean)),
  ];
  let status = null;
  if (statuses.length === 1) status = statuses[0];
  else if (statuses.length > 1) status = "MIXED";

  const settledTimes = transactions
    .map((row) => row.settledAt)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a));

  return {
    fromAccountNumber: accounts.join(", "),
    status,
    settledAt: settledTimes[0] || null,
    merchantFeePercent: samePercent(transactions, "merchantFeePercent"),
    merchantFee: sumPresent(transactions, "merchantFee"),
    xpayFeePercent: samePercent(transactions, "xpayFeePercent"),
    xpayFee: sumPresent(transactions, "xpayFee"),
    spayFee: sumPresent(transactions, "spayFee"),
    processingFeePercent: samePercent(transactions, "processingFeePercent"),
    processingFee: sumPresent(transactions, "processingFee"),
    afterFeeAmount: sumPresent(transactions, "afterFeeAmount"),
    ...settleView(transactions),
  };
}

/**
 * Paid instant from invoice.paidAt only. No proxy; legacy PAID without paidAt shows empty.
 */
export function invoicePaidAt(invoice) {
  if (invoice?.status !== "PAID") return null;
  if (!invoice.paidAt) return null;
  return invoice.paidAt;
}

export function settleStateLabel(state) {
  return SETTLE_LABEL[state] || SETTLE_LABEL.NONE;
}
